import crossIcon from '@xero/xui-icon/icons/cross';
import infoIcon from '@xero/xui-icon/icons/info';
import XUIButton from '@xero/xui/react/button';
import XUIIcon from '@xero/xui/react/icon';
import { XUIPickitem } from '@xero/xui/react/picklist';
import XUIPopover, { XUIPopoverBody, XUIPopoverFooter, XUIPopoverHeader } from '@xero/xui/react/popover';
import Fuse from 'fuse.js';
import React, { ChangeEvent, Dispatch, FC, SetStateAction, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { DropDownSelect } from '../../../../../components/DropDownSelect';
import { IAccount, IPaymentGatewayDetail, ITablePaymentGatewayDetail } from '../../../../../models';
import { IShopifySettingsResponse } from '../../../../../models/shopify';
import { bankAccountRemoved, paymentGatewayChanged } from '../../../../../redux/slices/AccountSetupSlice';
import { settingsSelectors } from '../../../../../redux/slices/SettingsSlice';
import { deepClone, useAppDispatch } from '../../../../../redux/store';
import { PaymentGatewayBankAccountDeletedState } from '../../models';
import { IDataRow } from './models';
import { formatOption } from './utils';

export interface IBankAccountDropdownProps {
    currentTableData: ITablePaymentGatewayDetail[];
    setCurrentTableData: Dispatch<SetStateAction<ITablePaymentGatewayDetail[]>>;
    dataRow: IDataRow;
    reconcilable?: boolean;
    lang: Record<string, any>;
    onGatewayChange: (paymentGatewayDetails: Record<string, IPaymentGatewayDetail>) => void;
    invalidRowsState: [string[], React.Dispatch<React.SetStateAction<string[]>>];
    paymentGatewayBankAccountDeletedState: PaymentGatewayBankAccountDeletedState;
}

export const BankAccountDropDown: FC<IBankAccountDropdownProps> = ({
    currentTableData,
    setCurrentTableData,
    dataRow,
    reconcilable,
    lang,
    onGatewayChange,
    invalidRowsState,
    paymentGatewayBankAccountDeletedState,
}) => {
    const dispatch = useAppDispatch();
    const triggerRef = useRef<HTMLDivElement>(null);
    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;
    const paymentGatewayDetails = deepClone(settings.paymentGatewayDetails);
    const bankAccounts = settings.bankAccounts;

    const [searchString, setSearchString] = useState('');
    const [invalidRows, setInvalidRows] = invalidRowsState;
    const [paymentGatewayBankAccountDeleted, setPaymentGatewayBankAccountDeleted] =
        paymentGatewayBankAccountDeletedState;
    const [showDeletedPopover, setShowDeletedPopover] = useState(paymentGatewayBankAccountDeleted[dataRow.paymentType]);

    const handleBankAccountSelection = (account: IAccount) => () => {
        if (paymentGatewayDetails) {
            paymentGatewayDetails[dataRow.paymentType].bankAccountId = account.accountId;
            paymentGatewayDetails[dataRow.paymentType].bankAccountCurrency = account.currencyCode;
            const index = currentTableData.findIndex(
                (item: { paymentType: string }) => item.paymentType === dataRow.paymentType
            );
            currentTableData[index].bankAccountId = account.accountId;
            setCurrentTableData(currentTableData);
            onGatewayChange(paymentGatewayDetails);
            dispatch(
                paymentGatewayChanged(dataRow.paymentType, {
                    ...paymentGatewayDetails[dataRow.paymentType],
                    bankAccountId: account.accountId,
                    bankAccountCurrency: account.currencyCode,
                })
            );
            setSearchString('');
            if (!dataRow.clearingAccount && invalidRows.indexOf(dataRow.paymentType) === -1) {
                setInvalidRows([...invalidRows, dataRow.paymentType]);
            }
        }
    };

    const handleDeselectBankAccountClick = () => {
        if (paymentGatewayDetails && paymentGatewayDetails[dataRow.paymentType].bankAccountId) {
            delete paymentGatewayDetails[dataRow.paymentType].bankAccountId;
            delete paymentGatewayDetails[dataRow.paymentType].bankAccountCurrency;
            const index = currentTableData.findIndex(
                (item: { paymentType: string }) => item.paymentType === dataRow.paymentType
            );
            delete currentTableData[index].bankAccountId;
            setCurrentTableData(currentTableData);
            onGatewayChange(paymentGatewayDetails);
            dispatch(bankAccountRemoved(dataRow.paymentType));
            const invalidRowIndex = invalidRows.findIndex((invalidRow) => invalidRow === dataRow.paymentType);
            if (invalidRowIndex !== -1) {
                invalidRows.splice(invalidRowIndex, 1);
                setInvalidRows([...invalidRows]);
            }
        }
    };

    const handleDismissBankAccountDeletedPopover = () => {
        setShowDeletedPopover(false);
        setPaymentGatewayBankAccountDeleted({ ...paymentGatewayBankAccountDeleted, [dataRow.paymentType]: false });
    };

    const { bankAccount, createInvoices } = dataRow;
    const validBankAccounts = useMemo(() => 
        bankAccounts
            ?.filter(
                (f) =>
                    paymentGatewayDetails &&
                    !Object.values(paymentGatewayDetails).some((s) => s.clearingAccountId === f.accountId) &&
                    (!dataRow.clearingAccount || dataRow.clearingAccount.currencyCode === f.currencyCode)
            )
            .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1)) ?? [],
            [bankAccounts, dataRow.clearingAccount, paymentGatewayDetails]
    );

    const handleInputChanges = (event: ChangeEvent<HTMLInputElement>) => setSearchString(event.target.value);
    const fuse = useMemo(
        () =>
            validBankAccounts
                ? new Fuse(validBankAccounts, {
                      keys: ['name'],
                  })
                : null,
        [validBankAccounts]
    );

    const fuzzySearchResult = fuse?.search(searchString).map((m) => m.item);
    const dropDownAccounts = fuzzySearchResult && fuzzySearchResult.length > 0 ? fuzzySearchResult : validBankAccounts;

    const bankAccountSelect = (
        <DropDownSelect
            buttonContent={bankAccount?.name}
            className='bank-account-selector'
            hideCaret={!!bankAccount}
            showSearchBox={reconcilable}
            onInputChange={handleInputChanges}
            onBlur={() => setSearchString('')}
        >
            {reconcilable ? (
                dropDownAccounts.map((m, i) => (
                    <XUIPickitem
                        key={i}
                        id={`bank-account-item-${i}`}
                        onClick={handleBankAccountSelection(m)}
                        isSelected={bankAccount?.accountId === m.accountId}
                    >
                        {formatOption(m.name, m.bankAccountNumber)}
                    </XUIPickitem>
                ))
            ) : (
                <div className='xui-padding xui-text-align-center'>
                    <XUIIcon icon={infoIcon} />
                    <br />
                    {lang.CANNOT_RECONCILE}
                </div>
            )}
        </DropDownSelect>
    );

    if (createInvoices) {
        return bankAccount ? (
            <div className='deselect-container'>
                {bankAccountSelect}
                <XUIButton
                    className='deselect-button'
                    onClick={handleDeselectBankAccountClick}
                    variant='borderless-standard'
                    qaHook='bank-account-deselect-button'
                >
                    <XUIIcon
                        icon={crossIcon}
                        color='black-faint'
                        className='xui-margin-top-2xsmall clear-account-icon'
                    />
                </XUIButton>
            </div>
        ) : (
            <div ref={triggerRef}>
                {bankAccountSelect}
                {showDeletedPopover && (
                    <XUIPopover
                        id='account-deleted-popover'
                        onClickOutside={handleDismissBankAccountDeletedPopover}
                        preferredPosition='bottom'
                        triggerRef={triggerRef}
                        onClickCloseButton={handleDismissBankAccountDeletedPopover}
                    >
                        <XUIPopoverHeader
                            closeButtonProps={{ ariaLabel: 'Close' }}
                            title={lang.BANK_ACCOUNT_DELETED}
                        />
                        <XUIPopoverBody>{lang.BANK_ACCOUNT_DELETED_MESSAGE}</XUIPopoverBody>
                        <XUIPopoverFooter
                            primaryAction={
                                <XUIButton onClick={handleDismissBankAccountDeletedPopover} variant='standard'>
                                    {lang.DISMISS_BUTTON}
                                </XUIButton>
                            }
                        />
                    </XUIPopover>
                )}
            </div>
        );
    } else {
        return <></>;
    }
};
